import { useFormContext, useWatch } from 'react-hook-form';
import { GWPField, GWPInput, GWPTextarea } from 'shared-components';
import ApplicationSection from '../ApplicationSection';
import { EntertainerApplicationContract, EntertainerApplicationFormState } from '../EntertainerApplicationTypes';
import {
	fieldError,
	formatList,
	formatMegabytes,
	promotionLinkFields,
	useApplicationFormUpdate,
} from '../EntertainerApplicationFormUtils';

type Props = { contract: EntertainerApplicationContract };

export default function PromotionSection({ contract }: Props) {
	const { control, formState } = useFormContext<EntertainerApplicationFormState>();
	const update = useApplicationFormUpdate();
	const [website, facebook, instagram, soundcloud, youtube, description] = useWatch({
		control,
		name: [
			'EntertainerWebsite',
			'EntertainerFacebook',
			'EntertainerInstagram',
			'EntertainerSoundcloud',
			'EntertainerYoutubeChannel',
			'EntertainerDescription',
		],
	});
	const socialFields = [
		['EntertainerWebsite', 'Website', website],
		['EntertainerFacebook', 'Facebook', facebook],
		['EntertainerInstagram', 'Instagram', instagram],
		['EntertainerSoundcloud', 'SoundCloud', soundcloud],
		['EntertainerYoutubeChannel', 'YouTube channel', youtube],
	] as const;
	const promotionLinksError = fieldError(formState.errors, promotionLinkFields[0]);

	return (
		<ApplicationSection
			title="Show us your work"
			description="Describe your act, attach an image of your act and provide at least one website or social profile."
		>
			<fieldset
				className={`entertainer-promotion-links${promotionLinksError ? ' invalid' : ''}`}
				aria-invalid={Boolean(promotionLinksError)}
			>
				<legend>
					Website or social profile{' '}
					<span className="entertainer-required-mark" aria-hidden="true">
						*
					</span>
				</legend>
				<p>Provide at least one of the following.</p>
				<div className="entertainer-grid two-columns">
					{socialFields.map(([field, label, value]) => (
						<GWPField key={field} label={label}>
							<GWPInput id={field} value={value} onValueChange={(next) => update(field, next)} />
						</GWPField>
					))}
				</div>
				{promotionLinksError ? (
					<p className="entertainer-field-error" role="alert">
						{promotionLinksError}
					</p>
				) : null}
			</fieldset>
			<GWPField
				label="Description of your act"
				required
				hint="What can audiences expect?"
				error={fieldError(formState.errors, 'EntertainerDescription')}
			>
				<GWPTextarea
					id="EntertainerDescription"
					value={description}
					onValueChange={(value) => update('EntertainerDescription', value)}
					rows={5}
				/>
			</GWPField>
			<GWPField
				label="Image of your act"
				required
				hint={`${formatList(contract.image.acceptedFormats)}, up to ${formatMegabytes(contract.image.maxBytes)}`}
				error={fieldError(formState.errors, 'customFile')}
			>
				<input
					id="customFile"
					className="entertainer-file-input"
					type="file"
					accept={contract.image.acceptedExtensions.map((extension) => `.${extension}`).join(',')}
					onChange={(event) => update('customFile', event.target.files?.[0] ?? null)}
				/>
			</GWPField>
		</ApplicationSection>
	);
}
